import { reportClientError } from './rollbar';

// Drop error events the browser can't describe. The canonical case is a
// cross-origin "Script error." — browsers strip filename/stack for scripts
// loaded without CORS, so the report is unactionable. Browser extensions,
// third-party trackers (Facebook in-app WebView, ad-blockers, etc.) are
// the usual offenders and swamp Rollbar with noise.
function isNoiseError(event: ErrorEvent): boolean {
  if (!event.message || event.message === 'Script error.' || event.message === 'Script error') {
    return true;
  }
  if (!event.filename) {
    return true;
  }
  const extensionPrefixes = [
    'chrome-extension://',
    'moz-extension://',
    'safari-web-extension://',
    'webkit-masked-url://',
  ];
  if (extensionPrefixes.some((p) => event.filename.startsWith(p))) {
    return true;
  }
  // Dev-only internal schemes come from our own code; keep them so local
  // debugging still reports. In production these don't appear.
  const internalPrefixes = ['webpack-internal://', 'webpack://'];
  if (internalPrefixes.some((p) => event.filename.startsWith(p))) {
    return false;
  }
  try {
    const url = new URL(event.filename);
    if (url.origin !== window.location.origin) {
      return true;
    }
  } catch {
    // Not a URL — allow through rather than hide a potential real error.
  }
  return false;
}

class GlobalErrorHandler {
  private static isInitialized = false;

  /**
   * Send error to Rollbar via API
   */
  private static async sendErrorToRollbar(errorData: {
    message: string;
    context: string;
    stack?: string;
    filename?: string;
    lineno?: number;
    colno?: number;
  }) {
    try {
      const response = await fetch('/api/notifications/error', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(errorData),
      });
      
      if (!response.ok) {
        console.warn('Error notification failed:', response.status, response.statusText);
      }
    } catch (notificationError) {
      console.warn('Failed to send error notification (Rollbar may not be configured):', notificationError);
    }
  }

  /**
   * Initialize global error handlers
   */
  static initialize() {
    if (this.isInitialized) {
      return;
    }

    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      console.error('Unhandled promise rejection:', event.reason);
      const pageInfo = window.location ? `Page: ${window.location.pathname}` : '';
      
      // Send to Rollbar directly
      reportClientError(event.reason?.message || String(event.reason), {
        operation: 'Unhandled Promise Rejection',
        additionalData: {
          pageInfo,
          stack: event.reason?.stack,
        },
      });
    });

    // Handle global JavaScript errors
    window.addEventListener('error', (event) => {
      if (isNoiseError(event)) {
        return;
      }
      console.error('Global JavaScript error:', event.error);
      const pathname = window.location?.pathname || '';

      // Send to Rollbar directly
      reportClientError(event.message, {
        component: pathname || 'Unknown Page',
        operation: 'Global JavaScript Error',
        additionalData: {
          pageInfo: pathname ? `Page: ${pathname}` : '',
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          stack: event.error?.stack,
        },
      });
    });

    // Handle console errors (for debugging)
    const originalConsoleError = console.error;
    let isReporting = false;
    console.error = (...args) => {
      originalConsoleError.apply(console, args);

      // Re-entry guard: Rollbar logs internal failures via console.error
      // (e.g. "Rollbar: Error while calling custom checkIgnore(), removing
      // TypeError: …"). Without this guard, our reporter would re-report
      // Rollbar's own logs back into Rollbar, recursing until the stack blows.
      if (isReporting) return;

      const errorMessage = args.join(' ');

      // Skip Rollbar's own internal logs — all prefixed with "Rollbar:".
      if (errorMessage.includes('Rollbar:')) return;

      // Only notify for actual errors, not warnings
      if (errorMessage.includes('Error:') || errorMessage.includes('TypeError:') || errorMessage.includes('ReferenceError:')) {
        const pageInfo = window.location ? `Page: ${window.location.pathname}` : '';

        isReporting = true;
        try {
          // Send to Rollbar directly
          reportClientError(errorMessage, {
            operation: 'Console Error',
            additionalData: {
              pageInfo,
            },
          });
        } finally {
          isReporting = false;
        }
      }
    };

    this.isInitialized = true;
    console.log('Global error handlers initialized');
  }

  /**
   * Handle network errors
   */
  static handleNetworkError(error: Error, context: string = 'Network Request') {
    console.error('Network error:', error);
    
    this.sendErrorToRollbar({
      message: error.message,
      context: `Network Error: ${context}`,
      stack: error.stack,
    });
  }

  /**
   * Handle fetch errors
   */
  static handleFetchError(response: Response, context: string = 'API Request') {
    console.error('Fetch error:', response.status, response.statusText);
    
    this.sendErrorToRollbar({
      message: `HTTP ${response.status}: ${response.statusText}`,
      context: `Fetch Error: ${context}`,
    });
  }
}

export default GlobalErrorHandler; 